import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ReviewInboxSource, ReviewRequest } from "./review-inbox";
import { reviewInboxEnabled, reviewInboxUsesMock } from "./review-inbox-flag";
import { createMockReviewSource, mockThreadRows } from "./review-inbox-mock";
import { createRpcReviewSource } from "./review-inbox-rpc";
import { type ThreadRow, threadRows, useLiveThread } from "./use-live-thread";

/** Badge fallback while no review frame arrives on boards.subscribe (contract, "Push"). */
const COUNT_POLL_MS = 30_000;

let source: ReviewInboxSource | null = null;

/**
 * The one source of the page and the badge, so both see the same cards. Development uses the
 * mock unless the flag says "rpc"; outside development there is no mock, only the server.
 */
export function reviewSource(): ReviewInboxSource {
  source ??= reviewInboxUsesMock() ? createMockReviewSource() : createRpcReviewSource();
  return source;
}

/** Tests start from fresh data. */
export function setReviewSourceForTest(next: ReviewInboxSource | null): void {
  source = next;
}

/** Query keys of the inbox; invalidating `all` reloads list, badge and the mock chats. */
export const reviewKeys = {
  all: ["review-inbox"] as const,
  list: ["review-inbox", "list"] as const,
  count: ["review-inbox", "count"] as const,
  thread: (botId: string, sessionId: string | null) =>
    ["review-inbox", "thread", botId, sessionId ?? ""] as const,
};

/** Open and deferred cards plus open chat cards; loads nothing while the inbox is off. */
export function useReviewList() {
  return useQuery({
    queryKey: reviewKeys.list,
    queryFn: () => reviewSource().list(),
    enabled: reviewInboxEnabled(),
  });
}

/** Open cards and chat cards for the sidebar badge; null while unknown or when the inbox is off. */
export function useReviewCount(): number | null {
  const enabled = reviewInboxEnabled();
  const query = useQuery({
    queryKey: reviewKeys.count,
    queryFn: () => reviewSource().count(),
    enabled,
    refetchInterval: COUNT_POLL_MS,
  });
  return enabled ? (query.data ?? null) : null;
}

/** After any change both the list and the badge load again. */
export function useInvalidateReviews() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: reviewKeys.all });
}

/** A review frame of boards.subscribe: a hint to reload list and badge, not state. */
export function isReviewFrame(event: { kind: string }): boolean {
  return event.kind === "review";
}

/**
 * The asking bot's chat for the card, from the card's creation on: live from the server, or the
 * mock chat in development. `failed` when the server chat could not load.
 */
export function useReviewThread(item: ReviewRequest | null): {
  rows: ThreadRow[] | null;
  failed: boolean;
} {
  const mock = reviewInboxUsesMock();
  const live = useLiveThread(
    item && !mock ? { botId: item.botId, sessionId: item.sessionId } : null,
  );
  const local = useQuery({
    queryKey: reviewKeys.thread(item?.botId ?? "", item?.sessionId ?? null),
    queryFn: () => (item ? mockThreadRows(item) : []),
    enabled: mock && item !== null,
  });
  if (!item) return { rows: null, failed: false };
  if (mock) return { rows: local.data ?? null, failed: false };
  return {
    rows: live.snapshot ? threadRows(live.snapshot.messages, item.createdAt) : null,
    failed: live.failed,
  };
}
